import React, { useEffect } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import Header from './components/Header';
import SiteFooter from './components/SiteFooter';
import HomePage from './pages/HomePage';
import ProductPage from './pages/ProductPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrdersPage from './pages/OrdersPage';
import WishlistPage from './pages/WishlistPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminProducts from './pages/AdminProducts';
import AdminProductForm from './pages/AdminProductForm';
import AdminPromotionalBanners from './pages/AdminPromotionalBanners';
import AdminOrders from './pages/AdminOrders';
import AdminAnalytics from './pages/AdminAnalytics';
import AdminDiscounts from './pages/AdminDiscounts';
import AdminPromotionalPictures from './pages/AdminPromotionalPictures';
import AdminCategoryCards from './pages/AdminCategoryCards';
import AdminContactInfo from './pages/AdminContactInfo';
import PaymentSelectionPage from './pages/PaymentSelectionPage';
import AdminLoginPage from './pages/AdminLoginPage';
import LogoTest from './pages/LogoTest';
import ProtectedRoute from './components/ProtectedRoute';
import AdminOrderNotifier from './components/AdminOrderNotifier';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { WishlistProvider } from './contexts/WishlistContext';
import { DarkModeProvider } from './contexts/DarkModeContext';
import api from './utils/api';

// Health check component to keep backend alive
const HealthCheck = () => {
  useEffect(() => {
    const pingBackend = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/health`);
        if (response.ok) {
          console.log('Backend health check successful');
        }
      } catch (error) {
        console.log('Backend health check failed:', error);
      }
    };

    // Ping immediately
    pingBackend();
    
    // Then ping every 10 minutes (600,000 ms)
    const interval = setInterval(pingBackend, 10 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  return null; // This component doesn't render anything
};

const VISITOR_STORAGE_KEY = 'tc_visitor_id';

const getOrCreateVisitorId = (): string => {
  const existing = window.localStorage.getItem(VISITOR_STORAGE_KEY);
  if (existing) return existing;

  const generated =
    typeof window.crypto?.randomUUID === 'function'
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;

  window.localStorage.setItem(VISITOR_STORAGE_KEY, generated);
  return generated;
};

const VisitTracker = () => {
  useEffect(() => {
    const trackVisit = async () => {
      try {
        const visitorId = getOrCreateVisitorId();
        await api.post(
          '/api/analytics/visit',
          {},
          {
            headers: {
              'x-visitor-id': visitorId
            }
          }
        );
      } catch (error) {
        console.error('Visitor tracking failed:', error);
      }
    };

    trackVisit();
  }, []);

  return null;
};

function App() {
  return (
    <DarkModeProvider>
      <WishlistProvider>
        <AuthProvider>
          <CartProvider>
            <HealthCheck />
            <VisitTracker />
            <AdminOrderNotifier />
            <div className="min-h-screen flex flex-col bg-white dark:bg-dark-bg transition-colors duration-300">
              <Header />
              <main className="flex-grow transition-colors duration-300">
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/product/:id" element={<ProductPage />} />
                  <Route path="/cart" element={<CartPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/checkout/payment" element={<PaymentSelectionPage />} />
                  <Route path="/orders" element={<OrdersPage />} />
                  <Route path="/wishlist" element={<WishlistPage />} />
                  <Route path="/login" element={<AdminLoginPage />} />
                  <Route path="/test" element={<div>Test Route Works!</div>} />
                  <Route path="/logo-test" element={<LogoTest />} />
                  <Route 
                    path="/admin" 
                    element={
                      <ProtectedRoute>
                        <AdminDashboard />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/admin/products" 
                    element={
                      <ProtectedRoute>
                        <AdminProducts />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/admin/products/new" 
                    element={
                      <ProtectedRoute>
                        <AdminProductForm />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/admin/products/edit/:id" 
                    element={
                      <ProtectedRoute>
                        <AdminProductForm />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/admin/promotional-banners" 
                    element={
                      <ProtectedRoute>
                        <AdminPromotionalBanners />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/admin/discounts" 
                    element={
                      <ProtectedRoute>
                        <AdminDiscounts />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/admin/orders" 
                    element={
                      <ProtectedRoute>
                        <AdminOrders />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/admin/analytics" 
                    element={
                      <ProtectedRoute>
                        <AdminAnalytics />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/admin/promotional-pictures" 
                    element={
                      <ProtectedRoute>
                        <AdminPromotionalPictures />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/admin/categories" 
                    element={
                      <ProtectedRoute>
                        <AdminCategoryCards />
                      </ProtectedRoute>
                    } 
                  />
                  <Route
                    path="/admin/contact-info"
                    element={
                      <ProtectedRoute>
                        <AdminContactInfo />
                      </ProtectedRoute>
                    }
                  />
                  {/* Catch-all route for client-side routing */}
                  <Route path="*" element={
                    <div className="min-h-screen bg-dark-bg flex items-center justify-center">
                      <div className="text-center">
                        <h1 className="text-2xl font-bold text-dark-text mb-4">Page not found</h1>
                        <p className="text-dark-textSecondary mb-4">Current path: {window.location.pathname}</p>
                        <Link to="/" className="text-pink-400 hover:text-pink-300">
                          Go to Home
                        </Link>
                      </div>
                    </div>
                  } />
                </Routes>
              </main>
              <SiteFooter />
            </div>
          </CartProvider>
        </AuthProvider>
      </WishlistProvider>
    </DarkModeProvider>
  );
}

export default App;
